import { useCallback, useState } from 'react'
import { preview } from '@/components/form/preview'
import { Tooltip } from '@/components/primitives/Tooltip'
import { MoreHoriz } from '@mui/icons-material'
import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material'
import { useTexts } from '../hooks/useTexts'
import { useCopyReasonsForNotCompletedOrdersModal } from '../../../modals/CopyReasonsForNotCompletedOrdersModal'

interface Props {
  reasonsForNotCompletedOrders: string[]
  territories: Record<string, uui.domain.client.rm.Territory>
}

export function ReasonsForNotCompletedOrders(props: Props) {
  const { reasonsForNotCompletedOrders, territories } = props
  const texts = useTexts()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const noReasons = reasonsForNotCompletedOrders.length === 0
  const singleTerritory = Object.keys(territories).length === 1

  const cannotCopyReasons = noReasons || singleTerritory
  const tooltipTitle = noReasons ? texts.noReasons : singleTerritory ? texts.oneTerritory : ''

  const {
    show: showCopyReasonsForNotCompletedOrdersModal,
    Modal: CopyReasonsForNotCompletedOrdersModal,
  } = useCopyReasonsForNotCompletedOrdersModal()

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleCopyClick = useCallback(() => {
    showCopyReasonsForNotCompletedOrdersModal()
    handleMenuClose()
  }, [showCopyReasonsForNotCompletedOrdersModal, handleMenuClose])

  return (
    <>
      <Stack direction={'row'} data-testid="driver-activity-reasons-not-completed-orders">
        <Box>
          <preview.Field
            label={`${texts.reasonsTitle} (${reasonsForNotCompletedOrders.length || 0})`}
          >
            {reasonsForNotCompletedOrders.map((reason, index) => (
              <preview.Value key={index}>{reason}</preview.Value>
            ))}
          </preview.Field>
        </Box>
        <Box marginLeft="auto" display="flex" alignItems="flex-start">
          <Tooltip title={texts.more} placement="top">
            <IconButton
              onClick={handleMenuClick}
              disabled={false}
              data-testid="driver-activity-reasons-not-completed-orders-menu-btn"
            >
              <MoreHoriz />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          <Tooltip title={tooltipTitle} placement="left" disabled={!cannotCopyReasons}>
            <MenuItem
              onClick={handleCopyClick}
              disabled={cannotCopyReasons}
              data-testid="driver-activity-reasons-not-completed-orders-copy-btn"
              data-trackid="driver-activity-reasons-not-completed-orders-copy-btn"
            >
              {texts.copySettingsToTerritory}
            </MenuItem>
          </Tooltip>
        </Menu>
      </Stack>

      <CopyReasonsForNotCompletedOrdersModal />
    </>
  )
}
